import React from 'react';
import * as R from 'ramda';
import { compose, withState, lifecycle } from 'recompose';
import styled from '@emotion/styled';
import { Box } from '@rebass/grid/emotion';
import '@influitive/infl-fonts/fonts.css';
import i18n from 'modules/localization';
import { I18nextProvider } from 'react-i18next';
import i18nMoment from '@influitive/moment-launcher';

import { qaSel, isMobileApp, isWhiteLabel, isMaven, updatePathMap, checkSelectedHub, windowLang, shouldLoadHubSwitcher } from './common/utils';
import constants from './common/constants';
import MainHeader from './components/main-header';
import ThemeProvider from './theme-provider';
import {
  hubConfig,
  customBranding,
  profile,
  features,
  hubSwitcher,
  getToken,
  enabledFeatures,
  unseenChallengesCounts,
  publicHomepageTabs,
  notificationsCount
} from './common/api';

const fontFamily = props => (
  props.theme.fonts.custom ? `${props.theme.fonts.custom.fontName}, ${constants.defaultFont}` : constants.defaultFont
);

const custom = props => `
  @font-face {
    font-family: ${props.theme.fonts.custom.fontName};
    font-style: normal;
    src: url(${props.theme.fonts.custom.fontUrl});
  }
`;

const App = styled(Box)`
  ${props => props.theme.fonts.custom && custom(props)}
  font-family: ${fontFamily};
`;

export default compose(
  withState('customBranding', 'brandingReceived', {}),
  withState('i18n', 'i18nReceived', null),
  withState('moment', 'momentReceived', null),
  withState('profile', 'profileReceived', null),
  withState('features', 'linksReceived', null),
  withState('hubSwitcher', 'hubSwitcherReceived', null),
  withState('isAuth', 'isAuthReceived', null),
  withState('allowOpenSignUp', 'openSignUpReceived', false),
  withState('publicTabs', 'publicTabsReceived', []),
  withState('enabledFeatures', 'enabledFeaturesReceived', null),
  withState('unseenCounts', 'unseenCountsReceived', {}),
  withState('unseenNotificationsCount', 'unseenNotificationsCountReceived', 0),
  lifecycle({
    componentDidMount() {
      hubConfig({ 'Content-Type': 'multipart/mixed' }, false).then((allowOpenSignUp) => {
        this.props.openSignUpReceived(allowOpenSignUp);
        const lang = windowLang();

        const options = { defaultNs: 'AdvocateNavFE', ns: 'AdvocateNavFE', lang };
        this.props.i18nReceived(i18n(options));

        const moment = i18nMoment({ lang });
        this.props.momentReceived(() => moment);

        publicHomepageTabs().then(this.props.publicTabsReceived);
        notificationsCount().then(data => this.props.unseenNotificationsCountReceived(data.count));
        customBranding().then(this.props.brandingReceived);
        getToken().then(() => {
          this.props.isAuthReceived(true);
          profile().then(this.props.profileReceived);
          features().then(this.props.linksReceived);
          shouldLoadHubSwitcher && hubSwitcher().then(hubs => {
            this.props.hubSwitcherReceived(hubs);
            unseenChallengesCounts(
              R.pluck('org_id',
                R.reject(checkSelectedHub, R.path(['family', 'companies'], hubs)))
            ).then(challengesCounts => {
              this.props.unseenCountsReceived(challengesCounts);
            });
          });
        }).catch(err => {
          if (err.response.status === 401) {
            this.props.isAuthReceived(false);
          }
        });
        enabledFeatures().then(features => {
          R.contains('discourse_homepage', features) && updatePathMap('forum', 'home');
          this.props.enabledFeaturesReceived(features);
        });
      }).catch(() => {
        this.props.isAuthReceived(false);
      });
    }
  })
)((props) => {
  return (
    <ThemeProvider {...props}>
      <I18nextProvider i18n={props.i18n}>
        <App data-qa={qaSel('app')} id="ADVOCATE-NAV">
          {(!isMobileApp() || isWhiteLabel || !isMaven) && <MainHeader {...props} />}
        </App>
      </I18nextProvider>
    </ThemeProvider>
  );
});
